<template src="./answer-reply-form.html"></template>

<script lang="ts">
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import createAnswerService from 'services/api/create-answer'
import createEmailAnswerService from 'services/api/create-email-answer'
import createEmailReplyService from 'services/api/create-email-reply'
import createReplyService from 'services/api/create-reply'
import domUtils from 'util/dom-utils'
import globalConfig from 'init/global.config'
import { hostsConfig } from 'init/config'
import { ISubmissionContext } from './answer-reply-submission-context'
import MultiItems from 'components/question/MultiItems.vue'
import { RestError } from 'services/api-service'
import Textarea from 'objects/form/Textarea.vue'
import tokenService from 'services/token-service'
import ssoService from 'services/sso-service'
import validator from 'util/validation-utils'


const ANSWER = 'answer'
const QUESTION = 'question'
const REPLY = 'reply'
const MAX_CHARACTERS = 2000

export default {
  data() {
    return {
      errorMsg: '',
      isSubmitting: false,
      multiItems: [],
      text: ''
    }
  },
  props: {
    hideCancel: Boolean,
    originSiteKey: String,
    parentType: {
      type: String,
      validator: value => value === REPLY || value === ANSWER || value === QUESTION
    },
    parent: {
      type: Object,
      required: true
    },
    questionId: Number
  },
  emits: ['close'],
  inject: {
    answerId: {
      from: 'answerId',
      default: null
    },
    submissionCtx: { // ISubmissionContext
      from: 'submissionCtx'
    }
  },
  components: {
    'tt-textarea': Textarea,
    'tt-multi-items': MultiItems
  },
  computed: {
    charactersLeft() {
      return MAX_CHARACTERS - this.text.length
    },
    forType() {
      return this.parentType === QUESTION ? 'answer' : 'reply'
    },
    label() {
      return this.$msg(`answerReplyForm.enter${this.parentType === QUESTION ? 'Answer' : 'Reply' }`)
    },
    fieldId() {
      return `tt-${this.forType}-form-field-${this.parent.id}`
    },
    maxCharacters() {
      return MAX_CHARACTERS
    },
    multiItemsPromptText() {
      return this.$msg(`multiItem.answer.prompt${this.multiItems.length > 0 ? '.withAdded' : ''}`)
    },
    showMultiItems() {
      return globalConfig.multiItemAnswerEnabled && this.parentType === QUESTION
    }
  },
  methods: {
    addMultiItems(items) {
      this.multiItems = [...items]
    },
    cancel() {
      this.$emit('close')
    },
    confirmedSubmitComplete(ugcType: 'answer' | 'reply') {
      (this.submissionCtx as ISubmissionContext).confirmedSubmitComplete({
        siteKey: this.originSiteKey || hostsConfig.siteKey,
        catItem: this.parent.catItem,
        questionId: this.questionId,
        ugcType
      })
    },
    focusOnField() {
      return domUtils.focus(this.$el.querySelector('textarea'))
    },
    removeMultiItem(item) {
      for (let i = 0; i < this.multiItems.length; i++) {
        if (this.multiItems[i].id === item.id) {
          this.multiItems.splice(i, 1)
          break
        }
      }
    },
    showError(e: RestError) {
      if (e.fieldErrors.length > 0 && e.fieldErrors[0].code === 'ext-site-link') {
        this.errorMsg = this.$msg('answerReplyForm.err.containsLink')
      } else {
        (this.submissionCtx as ISubmissionContext).showError(
          getOfflineOrUnexpectedMsg(e), false, true)
      }
      this.focusOnField().catch(() => {})
      this.isSubmitting = false
    },
    showRegistration(stagedContentToken: string, registerFor: 'answer' | 'reply') {
      (this.submissionCtx as ISubmissionContext).showRegistration({
        stagedContentToken, registerFor
      })
    },
    submitAnswerReply() {
      this.isSubmitting = true
      if (!this.validate()) {
        this.focusOnField().catch(() => {})
        this.isSubmitting = false
        return
      }
      // Coming from an email, the parent will have an answerToken (if question) or a
      // replyToken (if answer or reply)
      const emailToken = this.parent.answerToken || this.parent.replyToken
      if (emailToken) {
        if (this.parentType === QUESTION) {
          this.submitEmailAnswer(emailToken)
          return
        }
        this.submitEmailReply(emailToken)
        return
      }

      const confirmedToken = tokenService.getUserToken()
      const qualifier = confirmedToken ? 'confirmed' : 'unconfirmed'
      if (this.parentType === QUESTION) {
        this.submitInlineAnswer(qualifier, confirmedToken)
        return
      }
      this.submitInlineReply(qualifier, confirmedToken)
    },
    submitEmailAnswer(token) {
      createEmailAnswerService.createEmailAnswer({
        'context-token': token,
        originSiteKey: this.originSiteKey || '',
        sku: this.parent.catItem.sku
      }, {
        catItemId: this.parent.catItem.id,
        itemQuestionRecipientLogId: this.submissionCtx.iqrlId || null,
        multiItemIds: this.multiItems.map(item => item.id),
        questionId: this.parent.id,
        sourceId: 1,  // TODO: put on backend instead of passing?
        text: this.text
      }).then((data) => {
        (this.submissionCtx as ISubmissionContext).emailSubmitComplete('answer')
        this.onAnswerSubmit(data.answerId)
      }).catch((e: RestError) => {
        this.showError(e)
      })
    },
    submitEmailReply(token) {
      createEmailReplyService.createEmailReply({
        'context-token': token,
        originSiteKey: this.originSiteKey || '',
        sku: this.parent.catItem.sku
      }, {
        answerId: this.answerId,
        catItemId: this.parent.catItem.id,
        parentReplyId: this.parentType === REPLY ? this.parent.id : null,
        text: this.text
      }).then(() => {
        (this.submissionCtx as ISubmissionContext).emailSubmitComplete('reply')
        this.isSubmitting = false
      }).catch((e: RestError) => {
        this.showError(e)
      })
    },
    submitInlineAnswer(qualifier, token) {
      createAnswerService.createAnswer({
        'context-token': token,
        qualifier,
        originSiteKey: this.originSiteKey || '',
        sku: this.parent.catItem.sku
      }, {
        catItemId: this.parent.catItem.id,
        multiItemIds: this.multiItems.map(item => item.id),
        questionId: this.parent.id,
        sourceId: 2,  // TODO: put on backend instead of passing?
        text: this.text
      }).then((data) => {
        if (token) {
          this.confirmedSubmitComplete('answer')
          this.onAnswerSubmit(data.answerId)
        } else {
          if (globalConfig.ssoEnabled) {
            ssoService.ssoCreateAnswer(
              this.parent.catItem.sku, data.stagedAnswerToken, this.questionId)
          } else {
            this.showRegistration(data.stagedAnswerToken, 'answer')
          }
        }
        this.$emit('close')
        this.isSubmitting = false
      }).catch((e: RestError) => {
        this.showError(e)
      })
    },
    submitInlineReply(qualifier, token) {
      createReplyService.createReply({
        'context-token': token,
        qualifier,
        originSiteKey: this.originSiteKey || '',
        sku: this.parent.catItem.sku
      }, {
        answerId: this.answerId,
        catItemId: this.parent.catItem.id,
        parentReplyId: this.parentType === REPLY ? this.parent.id : null,
        text: this.text
      }).then((data) => {
        if (token) {
          this.confirmedSubmitComplete('reply')
        } else {
          if (globalConfig.ssoEnabled) {
            ssoService.ssoCreateReply(this.parent.catItem.sku, data.stagedReplyToken)
          } else {
            this.showRegistration(data.stagedReplyToken, 'reply')
          }
        }
        this.$emit('close', data)
        this.isSubmitting = false
      }).catch((e: RestError) => {
        this.showError(e)
      })
    },
    validate() {
      this.errorMsg = '' // clear any existing message
      if (this.text.trim().length > MAX_CHARACTERS) {
        return false
      }
      if (this.text.trim().length === 0) {
        this.errorMsg =
          this.$msg(`answerReplyForm.enter${this.parentType === QUESTION ? 'Answer' : 'Reply' }`)
        return false
      }
      if (validator.containsEmail(this.text)) {
        this.errorMsg = this.$msg('answerReplyForm.err.dontIncludeEmail')
        return false
      }
      if (validator.containsPhoneNumber(this.text)) {
        this.errorMsg = this.$msg('answerReplyForm.err.dontIncludePhone')
        return false
      }
      return true
    },
    onAnswerSubmit(answerId: number) {
      // if client provided an onAnswerSubmit function
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      if (eventHandlers.onAnswerSubmit) {
        eventHandlers.onAnswerSubmit({ id: answerId })
      }
    }
  },
  mounted() {
    this.focusOnField().then(() => {
      // if we have text from inbox submission, set it and submit
      if (this.submissionCtx?.answerText) {
        this.text = this.submissionCtx.answerText
        this.submitAnswerReply()
      }
    }).catch(() => {})
  }
}
</script>

<style lang="scss" src="./answer-reply-form.scss"></style>
